import { RTSignatureSection } from "@/components/usuarios/RTSignatureSection";
import { CertificadoDigitalCard, RTArtCard } from "@/components/usuarios/RTCredentials";
import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { useUsuarios, useEmpresas, useVersoesApp } from "@/hooks/use-data";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  Mail,
  Phone,
  UserPlus,
  X,
  Loader2,
  Pencil,
  Lock,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Search,
  Plus,
  Smartphone,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useState, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { api } from "@shared/routes";
import type { UserProfile } from "@shared/schema";
import {
  formatCpf,
  formatPhone,
  formatCep,
  unformatCep,
  unformatCpf,
  unformatPhone,
} from "@/lib/formatters";
import { ROLE_LABELS, ROLE_BADGE_COLORS, ALL_ROLES } from "@/lib/constants";
import { AlterarSenhaDialog } from "@/components/usuarios/AlterarSenhaDialog";
import { useModulos } from "@/hooks/use-modulos";
import { PreencherTesteButton } from "@/components/PreencherTesteButton";
import { gerarCpfFake, gerarCreaFake } from "@/lib/mock-test-data";
import { useIsEmpresaTestMode } from "@/hooks/use-modo-teste";

const ESTADOS_BR = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

// ─── Shared form fields ────────────────────────────────────────────────────────

interface UserFormState {
  nome: string;
  telefone: string;
  whatsapp: string;
  cpf: string;
  crea: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
}

function initialFormState(user?: UserProfile): UserFormState {
  return {
    nome: user?.nome ?? "",
    telefone: user?.telefone ? formatPhone(user.telefone) : "",
    whatsapp: user?.whatsapp ? formatPhone(user.whatsapp) : "",
    cpf: user?.cpf ? formatCpf(user.cpf) : "",
    crea: user?.crea ?? "",
    logradouro: user?.logradouro ?? "",
    numero: user?.numero ?? "",
    complemento: user?.complemento ?? "",
    bairro: user?.bairro ?? "",
    cidade: user?.cidade ?? "",
    estado: user?.estado ?? "",
    cep: user?.cep ? formatCep(user.cep) : "",
  };
}

function AdditionalFields({
  form,
  onChange,
  selectedRoles,
  isTestMode = false,
}: {
  form: UserFormState;
  onChange: (key: keyof UserFormState, value: string) => void;
  selectedRoles: string[];
  isTestMode?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const preencherTeste = () => {
    onChange("cpf", gerarCpfFake());
    if (selectedRoles.includes("rt")) {
      onChange("crea", gerarCreaFake(form.estado || "SP"));
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-sm font-medium hover:text-slate-700 transition-colors"
        >
          <span>Detalhes</span>
          {open ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>
        {isTestMode && <PreencherTesteButton onFill={preencherTeste} />}
      </div>
      {open && (
        <div className="p-4 grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="af-cpf">CPF</Label>
            <Input
              id="af-cpf"
              value={form.cpf}
              onChange={(e) => onChange("cpf", formatCpf(e.target.value))}
              placeholder="000.000.000-00"
              maxLength={14}
              inputMode="numeric"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="af-whatsapp">WhatsApp</Label>
            <Input
              id="af-whatsapp"
              value={form.whatsapp}
              onChange={(e) =>
                onChange("whatsapp", formatPhone(e.target.value))
              }
              placeholder="(00) 00000-0000"
              maxLength={15}
              inputMode="numeric"
            />
          </div>
          {selectedRoles.includes("rt") && (
            <div className="space-y-1.5">
              <Label htmlFor="af-crea">CREA</Label>
              <Input
                id="af-crea"
                value={form.crea}
                onChange={(e) => onChange("crea", e.target.value)}
                placeholder="CREA-UF 000000"
              />
            </div>
          )}
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="af-logradouro">Logradouro</Label>
            <Input
              id="af-logradouro"
              value={form.logradouro}
              onChange={(e) => onChange("logradouro", e.target.value)}
              placeholder="Rua, Av., etc."
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="af-numero">Número</Label>
            <Input
              id="af-numero"
              value={form.numero}
              onChange={(e) => onChange("numero", e.target.value)}
              placeholder="123"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="af-complemento">Complemento</Label>
            <Input
              id="af-complemento"
              value={form.complemento}
              onChange={(e) => onChange("complemento", e.target.value)}
              placeholder="Apto, Sala…"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="af-bairro">Bairro</Label>
            <Input
              id="af-bairro"
              value={form.bairro}
              onChange={(e) => onChange("bairro", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="af-cidade">Cidade</Label>
            <Input
              id="af-cidade"
              value={form.cidade}
              onChange={(e) => onChange("cidade", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Estado</Label>
            <Select
              value={form.estado || "__none__"}
              onValueChange={(v) =>
                onChange("estado", v === "__none__" ? "" : v)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="UF" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Selecione —</SelectItem>
                {ESTADOS_BR.map((uf) => (
                  <SelectItem key={uf} value={uf}>
                    {uf}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="af-cep">CEP</Label>
            <Input
              id="af-cep"
              value={form.cep}
              onChange={(e) => onChange("cep", formatCep(e.target.value))}
              placeholder="00000-000"
              maxLength={9}
              inputMode="numeric"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CreateUsuarioForm ─────────────────────────────────────────────────────────

function CreateUsuarioForm({
  onClose,
  isOwner,
  isAdmin,
  fixedEmpresaId,
}: {
  onClose: () => void;
  isOwner: boolean;
  isAdmin: boolean;
  fixedEmpresaId?: string;
}) {
  const { session } = useAuth();
  const { toast } = useToast();
  const { data: empresas } = useEmpresas();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [empresaId, setEmpresaId] = useState(fixedEmpresaId ?? "");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const selectedEmpresaTipo = empresas?.find((e) => e.id === empresaId)?.tipo;
  const isTestMode = !!(empresas?.find((e) => e.id === empresaId) as { modoTeste?: boolean } | undefined)?.modoTeste;
  const isParceiro = selectedEmpresaTipo === "PARCEIRO_DE_NEGOCIOS";
  const rolesDisponiveis = ALL_ROLES.filter((role) => {
    if (role === "owner") return isOwner;
    if (isParceiro) return role === "parceiro_negocios";
    return role !== "parceiro_negocios";
  });
  const [telefone, setTelefone] = useState("");
  const [form, setForm] = useState<UserFormState>(initialFormState());

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch(api.usuarios.create.path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao criar usuário");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.usuarios.list.path] });
      toast({ title: "Usuário criado com sucesso." });
      onClose();
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  function toggleRole(role: string) {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    mutation.mutate({
      nome: form.nome,
      email,
      password,
      empresaId,
      roles: selectedRoles,
      telefone: telefone ? unformatPhone(telefone) : undefined,
      whatsapp: form.whatsapp ? unformatPhone(form.whatsapp) : undefined,
      cpf: form.cpf ? unformatCpf(form.cpf) : undefined,
      crea: form.crea || undefined,
      logradouro: form.logradouro || undefined,
      numero: form.numero || undefined,
      complemento: form.complemento || undefined,
      bairro: form.bairro || undefined,
      cidade: form.cidade || undefined,
      estado: form.estado || undefined,
      cep: form.cep ? unformatCep(form.cep) : undefined,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="u-nome">Nome *</Label>
          <Input
            id="u-nome"
            value={form.nome}
            onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
            placeholder="Nome completo"
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="u-email">E-mail *</Label>
          <Input
            id="u-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="usuario@email.com"
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="u-password">Senha *</Label>
          <div className="relative">
            <Input
              id="u-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres"
              className="pr-10"
              required
              minLength={6}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="u-telefone">Telefone</Label>
          <Input
            id="u-telefone"
            value={telefone}
            onChange={(e) => setTelefone(formatPhone(e.target.value))}
            placeholder="(00) 00000-0000"
            maxLength={15}
            inputMode="numeric"
          />
        </div>
      </div>

      {isOwner || isAdmin ? (
        <div className="space-y-1.5">
          <Label>Empresa *</Label>
          <Select
            value={empresaId}
            onValueChange={(id) => {
              setEmpresaId(id);
              setSelectedRoles([]);
            }}
            required
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione a empresa" />
            </SelectTrigger>
            <SelectContent>
              {empresas?.map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>
                  {emp.nomeFantasia || emp.razaoSocial}{" "}
                  <span className="text-xs text-muted-foreground ml-1">
                    ({emp.tipo})
                  </span>
                  {["OWNER", "VISTORIADORA"].filter((e) => e != emp.tipo) && (
                    <span className="text-xs text-muted-foreground ml-1">
                      ({emp.idProprio ?? "Sem ID Próprio"})
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label>Empresa</Label>
          <p className="text-sm text-slate-600 px-3 py-2 bg-slate-50 rounded-md border">
            {empresas?.find((e) => e.id === empresaId)?.nomeFantasia ||
              empresas?.find((e) => e.id === empresaId)?.razaoSocial ||
              "—"}
          </p>
        </div>
      )}

      <div className="space-y-1.5">
        <Label>Perfis *</Label>
        <div className="flex flex-wrap gap-2">
          {rolesDisponiveis.map((role) => (
            <button
              key={role}
              type="button"
              onClick={() => toggleRole(role)}
              className={cn(
                "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                selectedRoles.includes(role)
                  ? cn(ROLE_BADGE_COLORS[role], "border-current")
                  : "bg-white text-slate-500 border-slate-200 hover:border-slate-400",
              )}
            >
              {ROLE_LABELS[role]}
            </button>
          ))}
        </div>
        {selectedRoles.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Selecione ao menos um perfil.
          </p>
        )}
      </div>

      <AdditionalFields
        form={form}
        selectedRoles={selectedRoles}
        onChange={(key, val) => setForm((f) => ({ ...f, [key]: val }))}
        isTestMode={isTestMode}
      />

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={
            mutation.isPending ||
            !form.nome ||
            !email ||
            !password ||
            !empresaId ||
            selectedRoles.length === 0
          }
        >
          {mutation.isPending && (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          )}
          Criar usuário
        </Button>
      </div>
    </form>
  );
}

// ─── EditUsuarioForm ───────────────────────────────────────────────────────────

function EditUsuarioForm({
  user,
  onClose,
  isOwner,
}: {
  user: UserProfile;
  onClose: () => void;
  isOwner: boolean;
}) {
  const { session } = useAuth();
  const { toast } = useToast();
  const isTestMode = useIsEmpresaTestMode(user.empresaId);

  const [selectedRoles, setSelectedRoles] = useState<string[]>(user.roles);
  const [telefone, setTelefone] = useState(
    user.telefone ? formatPhone(user.telefone) : "",
  );
  const [form, setForm] = useState<UserFormState>(initialFormState(user));

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch(`${api.usuarios.list.path}/${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao atualizar usuário");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.usuarios.list.path] });
      toast({ title: "Usuário atualizado com sucesso." });
      onClose();
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  function toggleRole(role: string) {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (selectedRoles.includes("rt")) {
      if (!form.crea.trim()) {
        toast({
          title: "CREA obrigatório",
          description: "Preencha o CREA antes de salvar o Responsável Técnico.",
          variant: "destructive",
        });
        return;
      }
    }
    mutation.mutate({
      nome: form.nome,
      roles: selectedRoles,
      telefone: telefone ? unformatPhone(telefone) : null,
      whatsapp: form.whatsapp ? unformatPhone(form.whatsapp) : null,
      cpf: form.cpf ? unformatCpf(form.cpf) : null,
      crea: form.crea || null,
      logradouro: form.logradouro || null,
      numero: form.numero || null,
      complemento: form.complemento || null,
      bairro: form.bairro || null,
      cidade: form.cidade || null,
      estado: form.estado || null,
      cep: form.cep ? unformatCep(form.cep) : null,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="eu-nome">Nome *</Label>
          <Input
            id="eu-nome"
            value={form.nome}
            onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
            placeholder="Nome completo"
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label>E-mail</Label>
          <p className="text-sm text-slate-600 px-3 py-2 bg-slate-50 rounded-md border truncate">
            {user.email}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="eu-telefone">Telefone</Label>
          <Input
            id="eu-telefone"
            value={telefone}
            onChange={(e) => setTelefone(formatPhone(e.target.value))}
            placeholder="(00) 00000-0000"
            maxLength={15}
            inputMode="numeric"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Perfis *</Label>
        <div className="flex flex-wrap gap-2">
          {ALL_ROLES.filter((role) => isOwner || role !== "owner").map(
            (role) => (
              <button
                key={role}
                type="button"
                onClick={() => toggleRole(role)}
                className={cn(
                  "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                  selectedRoles.includes(role)
                    ? cn(ROLE_BADGE_COLORS[role], "border-current")
                    : "bg-white text-slate-500 border-slate-200 hover:border-slate-400",
                )}
              >
                {ROLE_LABELS[role]}
              </button>
            ),
          )}
        </div>
        {selectedRoles.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Selecione ao menos um perfil.
          </p>
        )}
      </div>

      <AdditionalFields
        form={form}
        selectedRoles={selectedRoles}
        onChange={(key, val) => setForm((f) => ({ ...f, [key]: val }))}
        isTestMode={isTestMode}
      />

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={
            mutation.isPending || !form.nome || selectedRoles.length === 0
          }
        >
          {mutation.isPending && (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          )}
          Salvar alterações
        </Button>
      </div>
    </form>
  );
}

// ─── RT Signature Section ──────────────────────────────────────────────────────

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function Usuarios() {
  const { profile } = useAuth();
  const isOwner = profile?.roles?.includes("owner") ?? false;
  const isAdmin = profile?.roles?.includes("admin") ?? false;
  const canCreate = isOwner || isAdmin;
  const { modulos } = useModulos();

  // Owner vê todos; admin vê sua empresa + parceiros (via vistoriadoraId); demais só a sua
  const { data: usuarios, isLoading } = useUsuarios(
    !isOwner && !isAdmin ? profile?.empresaId : undefined,
    true,
    !isOwner && isAdmin ? profile?.empresaId : undefined,
  );
  const { data: empresas } = useEmpresas();
  const { data: versoesApp } = useVersoesApp(isOwner);
  const versaoAppPorUsuario = useMemo(
    () => new Map((versoesApp ?? []).map((v) => [v.userId, v])),
    [versoesApp],
  );

  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<UserProfile | undefined>();
  const [userParaResetarSenha, setUserParaResetarSenha] = useState<
    UserProfile | undefined
  >();
  const [openResetSenha, setOpenResetSenha] = useState(false);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");

  const usuariosFiltrados = useMemo(() => {
    if (!usuarios) return undefined;
    let list = usuarios;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (u) =>
          u.nome.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q),
      );
    }
    if (filterRole !== "all") {
      list = list.filter((u) => u.roles.includes(filterRole));
    }
    return list;
  }, [usuarios, search, filterRole]);

  const empresasGerentiadasIds = new Set(empresas?.map((e) => e.id) ?? []);

  function canEdit(user: UserProfile) {
    if (isOwner) return true;
    if (isAdmin && empresasGerentiadasIds.has(user.empresaId)) return true;
    return false;
  }

  // Agrupar usuários por empresa (usa lista filtrada)
  const usuariosPorEmpresa = usuariosFiltrados
    ? Object.groupBy(usuariosFiltrados, (u) => u.empresaId)
    : {};

  // Ordenar usuários pela role de maior hierarquia: owner → admin → rt → tv → parceiro_negocios
  const roleOrder: Record<string, number> = {
    owner: 0,
    admin: 1,
    rt: 2,
    tv: 3,
    parceiro_negocios: 4,
  };
  const sortUsersByRole = (users: typeof usuarios) =>
    users
      ? [...users].sort((a, b) => {
          const rankA = Math.min(...a.roles.map((r) => roleOrder[r] ?? 99));
          const rankB = Math.min(...b.roles.map((r) => roleOrder[r] ?? 99));
          return rankA - rankB;
        })
      : [];

  // Ordenar empresas por tipo: OWNER → VISTORIADORA → PARCEIRO_DE_NEGOCIOS
  // Deriva a lista a partir dos usuários para garantir que todas as empresas aparecem,
  // mesmo que useEmpresas não retorne as empresas parceiras para admins de vistoriadora.
  const tipoOrder: Record<string, number> = {
    OWNER: 0,
    VISTORIADORA: 1,
    PARCEIRO_DE_NEGOCIOS: 2,
  };
  const empresasOrdenadas = Object.keys(usuariosPorEmpresa)
    .map(
      (empresaId) =>
        empresas?.find((e) => e.id === empresaId) ?? {
          id: empresaId,
          tipo: "PARCEIRO_DE_NEGOCIOS" as const,
        },
    )
    .filter((empresa) => isOwner || empresa.tipo !== "OWNER")
    .sort((a, b) => (tipoOrder[a.tipo] ?? 99) - (tipoOrder[b.tipo] ?? 99));

  return (
    <Shell>
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1
            className="text-3xl font-bold font-display text-slate-900"
            data-testid="text-page-title"
          >
            Usuários
          </h1>
          <p className="text-muted-foreground mt-1">Equipe da empresa</p>
        </div>
        {canCreate && !editingUser && (
          <Button
            onClick={() => setShowForm((v) => !v)}
            variant={showForm ? "outline" : "default"}
          >
            {showForm ? (
              <>
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4 mr-2" />
                Novo usuário
              </>
            )}
          </Button>
        )}
      </div>

      {canCreate && showForm && !editingUser && (
        <div className="mb-8 p-6 rounded-xl border bg-white shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Novo usuário</h2>
          <CreateUsuarioForm
            onClose={() => setShowForm(false)}
            isOwner={isOwner}
            isAdmin={isAdmin}
            fixedEmpresaId={isOwner ? undefined : profile?.empresaId}
          />
        </div>
      )}

      {editingUser && (
        <div className="mb-8 p-6 rounded-xl border bg-white shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Editar usuário</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setEditingUser(undefined)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <EditUsuarioForm
            user={editingUser}
            onClose={() => setEditingUser(undefined)}
            isOwner={isOwner}
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nome ou e-mail..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9"
          />
        </div>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-[160px] h-9">
            <SelectValue placeholder="Todos os perfis" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os perfis</SelectItem>
            {Object.entries(ROLE_LABELS).map(([role, label]) => (
              <SelectItem key={role} value={role}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(search || filterRole !== "all") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch("");
              setFilterRole("all");
            }}
          >
            <X className="w-4 h-4 mr-1" />
            Limpar
          </Button>
        )}
      </div>

      <div className="space-y-8">
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array(3)
              .fill(0)
              .map((_, i) => (
                <Skeleton key={i} className="h-48 rounded-xl" />
              ))}
          </div>
        ) : usuariosFiltrados && usuariosFiltrados.length > 0 ? (
          empresasOrdenadas.map((empresa) => {
            const users = usuariosPorEmpresa[empresa.id];
            return (
              <div key={empresa.id} className="space-y-4">
                <div className="border-b border-slate-200 pb-2">
                  <h2 className="text-lg font-semibold text-slate-900">
                    {(empresa as any).nomeFantasia ||
                      (empresa as any).razaoSocial ||
                      empresa.id}
                  </h2>
                  <p className="text-sm text-slate-500">
                    {users?.length || 0} usuário{users?.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {sortUsersByRole(users)?.map((user) => (
                    <Card
                      key={user.id}
                      className="border-none shadow-md hover:shadow-lg transition-shadow"
                      data-testid={`card-usuario-${user.id}`}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600">
                              {user.nome.substring(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <CardTitle className="text-base">
                                {user.nome}
                              </CardTitle>
                              <div className="flex gap-1 mt-1 flex-wrap">
                                {user.roles.map((role) => (
                                  <Badge
                                    key={role}
                                    variant="secondary"
                                    className={cn(
                                      "text-xs",
                                      ROLE_BADGE_COLORS[role] ||
                                        "bg-slate-100 text-slate-700",
                                    )}
                                  >
                                    {ROLE_LABELS[role] || role.toUpperCase()}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {!user.ativo && (
                              <Badge variant="destructive" className="text-xs">
                                Inativo
                              </Badge>
                            )}
                            {canEdit(user) && (
                              <>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="w-8 h-8 text-slate-400 hover:text-slate-700"
                                  title="Editar usuário"
                                  onClick={() => {
                                    setShowForm(false);
                                    setEditingUser(user);
                                  }}
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="w-8 h-8 text-slate-400 hover:text-slate-700"
                                  title="Redefinir senha"
                                  onClick={() => {
                                    setUserParaResetarSenha(user);
                                    setOpenResetSenha(true);
                                  }}
                                >
                                  <Lock className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-slate-500">
                          <Mail className="w-3.5 h-3.5" />
                          <span className="truncate">{user.email}</span>
                        </div>
                        {user.telefone && (
                          <div className="flex items-center gap-2 text-slate-500">
                            <Phone className="w-3.5 h-3.5" />
                            <span>{user.telefone}</span>
                          </div>
                        )}
                        {user.cpf && (
                          <p className="text-slate-500">
                            CPF: {formatCpf(user.cpf)}
                          </p>
                        )}
                        {user.crea && (
                          <p className="text-slate-500">CREA: {user.crea}</p>
                        )}
                        {versaoAppPorUsuario.get(user.id) && (
                          <div
                            className="flex items-center gap-2 text-slate-500"
                            title="Versão informada pelo APP no último login por documento"
                            data-testid={`versao-app-${user.id}`}
                          >
                            <Smartphone className="w-3.5 h-3.5" />
                            <span>
                              APP {versaoAppPorUsuario.get(user.id)!.versao} ·{" "}
                              {new Date(versaoAppPorUsuario.get(user.id)!.vistoEm).toLocaleDateString("pt-BR")}
                            </span>
                          </div>
                        )}
                        {user.rebaixadoEm && (
                          <p className="text-xs text-red-500 mt-1">
                            Rebaixado em:{" "}
                            {new Date(user.rebaixadoEm).toLocaleDateString(
                              "pt-BR",
                            )}
                          </p>
                        )}
                        {user.roles.includes("rt") && (
                          <RTSignatureSection
                            user={user}
                            canManage={canEdit(user)}
                          />
                        )}
                        {user.roles.includes("rt") && (
                          <CertificadoDigitalCard
                            user={user}
                            canManage={isAdmin && !isOwner && canEdit(user)}
                            canView={canEdit(user)}
                          />
                        )}
                        {user.roles.includes("rt") && modulos.artPorRt && (
                          <RTArtCard
                            user={user}
                            canManage={canEdit(user)}
                            isTestMode={!!(empresa as { modoTeste?: boolean }).modoTeste}
                          />
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            Nenhum usuário encontrado
          </div>
        )}
      </div>

      {userParaResetarSenha && (
        <AlterarSenhaDialog
          open={openResetSenha}
          onOpenChange={setOpenResetSenha}
          usuarioId={userParaResetarSenha.id}
          usuarioNome={userParaResetarSenha.nome}
          isOwnPassword={false}
          onSuccess={() => {
            setUserParaResetarSenha(undefined);
            setOpenResetSenha(false);
          }}
        />
      )}
    </Shell>
  );
}
